<!-- A2 — Der Assistent lebt ausschliesslich als Reiter im Help-Hub
     (help-hub.component.ts, `<app-help-chat [embedded]="true">` — der einzige Aufrufer).
     Identität (Avatar, Name, Status, Kontingent-Chip, „Neue Unterhaltung") und der
     Schliessen-Knopf trägt `.hub-header`; eine zweite Kopfzeile sagte dasselbe ein zweites
     Mal und kostete 52,8 px.
     ⚠️ Kein `role="dialog"`/`aria-modal`/`cdkTrapFocus` mehr: Das Panel IST der Dialog
     (`aria-modal="false"`, nicht-modaler Intercom-Stil), und eine Fokus-Falle in einem
     Reiter sperrt den Fokus im Reiter ein — genau deshalb war sie schon vorher an
     `!embedded` gehängt und damit nie aktiv. -->
<section
  class="help-chat"
  role="region"
  [attr.aria-label]="'HELP_CHAT.ASSISTANT_NAME' | translate"
>
  <div class="help-chat__body" #scrollEl>
    <ng-container *ngIf="chat.isEmpty(); else conversation">
      <!-- 12.10.2026: Platz für den Hinweis „Tour zu dieser Seite“, den der Hilfe-Hub
           hineinreicht (app-page-tour-button variant="hint"). Nur im leeren Chat — mitten in
           einer Unterhaltung wäre er eine Unterbrechung. -->
      <ng-content select="[helpChatEmptyLead]"></ng-content>
      <!-- C2 — Sprechen wird sichtbar. Für Schichtleitung mit vollen Händen ist Sprache
           der schnellste Weg; bis 2026-09-09 war sie ein 38px-Knopf am Rand, und der
           Hinweis „Halten zum Aufnehmen" stand in einem title, das auf Touch nie
           erscheint. Antippen startet die Freihand-Aufnahme. -->
      <button
        xdsActionItem
        layout="rich"
        type="button"
        class="voice-cta"
        onboardingAnchor="assistant-voice-cta"
        *ngIf="voice.isSupported && voice.state() === 'idle' && !voiceDraft"
        (click)="onVoiceCta()"
      >
        <span class="voice-cta__ring" aria-hidden="true">
          <i class="fa-solid fa-microphone"></i>
        </span>
        <span class="voice-cta__text">
          <span class="voice-cta__title">{{ 'HELP_CHAT.VOICE.CTA_TITLE' | translate }}</span>
          <!-- C2 · Die zwei Aufnahmearten als Beschriftung. Beide laufen seit dem
               09.09.2026, standen aber nur als Nebensatz in einem Untertitel („Halten zum
               Sprechen · oder tippen für Freihand") — eine Zeile, in der zwei Bedienarten
               um dieselbe Aufmerksamkeit konkurrieren. Jetzt trägt jeder Modus seine eigene
               Marke, und der Untertitel ist ERSATZLOS weg: beides zusammen sagte dasselbe
               zweimal.

               ⚠️ **xds-badge, NICHT xds-chip** — und das ist keine Geschmacksfrage. Der Kit
               sagt es in der Chip-Doku selbst: „Für schreibgeschützte Status: xds-badge."
               Ein Chip ist ein Bedienelement und trägt Kontrollhöhe; am Gerät gemessen
               (390×844, 10.09.2026): Chip 32 px, Badge 22 px. Mit Chips war dieser Einstieg
               67,6 px hoch und damit 6 px HÖHER als vor dem Umbau (61,6) — die
               Nutzer-Rückmeldung „nimmt viel zu viel Platz" war die Quittung dafür, ein
               Bedienelement als Beschriftung benutzt zu haben. Mit Badges: 56 px.

               ⚠️ Die Beschriftungen sind EIN Wort. „Halten zum Sprechen" + „Tippen für
               Freihand" brauchen zusammen mehr als die Textspalte hergibt (306 px bei
               390er Gerät), brechen um und stapeln. Die Langform steht weiter auf der
               Mikro-Taste (title=MIC_TITLE), wo sie beim Zeigen erscheint. -->
          <span class="voice-cta__modes">
            <xds-badge variant="neutral" icon="fa-solid fa-hand-pointer">{{
              'HELP_CHAT.VOICE.MODE_HOLD' | translate
            }}</xds-badge>
            <xds-badge variant="neutral" icon="fa-solid fa-microphone-lines">{{
              'HELP_CHAT.VOICE.MODE_HANDSFREE' | translate
            }}</xds-badge>
          </span>
        </span>
      </button>

      <!-- `[tourContextKey]` entfällt mit dem Dialog-Modus: Der Wert kam ausschliesslich
           aus `MAT_DIALOG_DATA`, und ohne Dialog gibt es niemanden, der ihn setzt. Die
           Bindung stand hier zuletzt als konstantes `null` — der Zusatz-Chip für die
           laufende Tour war damit unerreichbar, nicht nur unbenutzt. Der `@Input()` in
           `suggested-questions` bleibt: er ist ein Angebot, kein toter Zweig. -->
      <app-help-suggested-questions
        (chipSelected)="onChipSelect($event)"
      ></app-help-suggested-questions>
    </ng-container>

    <ng-template #conversation>
      <div class="bubbles">
        <ng-container *ngFor="let turn of chat.turns(); let last = last">
          <app-help-message-bubble
            *ngIf="turn.role === 'user' || !turn.blocks?.length"
            [role]="turn.role"
            [content]="turn.content"
            (linkClick)="onLinkClick($event)"
          ></app-help-message-bubble>

          <!-- Strukturierte Antwort (Phase C): native Karten statt Text-Bubble. -->
          <app-chat-blocks
            *ngIf="turn.role === 'assistant' && turn.blocks?.length"
            [blocks]="turn.blocks!"
            (rowAction)="onRowAction($event)"
          ></app-chat-blocks>

          <button
            *ngIf="guideTourKey(turn) as gk"
            type="button"
            class="guide-cta"
            (click)="onRequestGuide(gk)"
          >
            <span class="guide-cta__icon" aria-hidden="true">▶</span>
            {{ 'HELP_CHAT.SHOW_STEPS' | translate }}
          </button>

          <ng-container *ngIf="turn.role === 'assistant' && turn.sources?.length">
            <div class="sources">
              <small class="sources__label">
                {{ 'HELP_CHAT.SOURCES_LABEL' | translate }}:
              </small>
              <app-help-source-card
                *ngFor="let src of turn.sources"
                [source]="src"
                (activate)="onSourceActivate($event)"
              ></app-help-source-card>
            </div>
          </ng-container>

          <ng-container *ngIf="turn.role === 'assistant' && turn.related?.length">
            <div class="related">
              <small class="related__label">
                {{ 'HELP_CHAT.RELATED_LABEL' | translate }}:
              </small>
              <button
                *ngFor="let r of turn.related"
                type="button"
                class="related__chip"
                (click)="onRelatedClick(r)"
              >
                {{ r.title }}
              </button>
            </div>
          </ng-container>

          <ng-container *ngIf="turn.role === 'assistant' && turn.feedbackId">
            <app-help-feedback-buttons
              [feedbackId]="turn.feedbackId!"
              (rated)="onFeedback($event)"
            ></app-help-feedback-buttons>
          </ng-container>

          <!-- Review e (02.11.2026): Auswege, wenn die Hilfe nichts gefunden hat. Die Texte
               (HELP_CHAT.NO_MATCH / NO_MATCH_HINTS) standen seit Mai in jeder Sprachdatei,
               keine Fläche las sie — die Absage endete ohne Weg weiter. Nur unter der
               LETZTEN Antwort: weiter oben im Verlauf wären es Knöpfe zu einer alten Frage.
               Der Satz NO_MATCH steht nur als Gruppenname (Vorleser): sichtbar sagt die
               Absage des Servers direkt darüber bereits dasselbe. -->
          <div
            *ngIf="turn.role === 'assistant' && turn.noMatch && last"
            class="related no-match-hints"
            data-testid="help-chat-no-match-hints"
            role="group"
            [attr.aria-label]="'HELP_CHAT.NO_MATCH' | translate"
          >
            <button
              type="button"
              class="related__chip"
              data-testid="help-chat-no-match-rephrase"
              (click)="onNoMatchRephrase()"
            >
              {{ 'HELP_CHAT.NO_MATCH_HINTS.REPHRASE' | translate }}
            </button>
            <button
              *ngIf="noMatchTourId() as tourId"
              type="button"
              class="related__chip"
              data-testid="help-chat-no-match-tour"
              (click)="onRequestGuide(tourId)"
            >
              {{ 'HELP_CHAT.NO_MATCH_HINTS.TOUR' | translate }}
            </button>
            <a
              class="related__chip"
              data-testid="help-chat-no-match-support"
              [href]="supportMailto"
            >
              {{ 'HELP_CHAT.NO_MATCH_HINTS.SUPPORT' | translate }}
            </a>
          </div>

          <!-- B2 — Vorschau-Karte für eine Schreibaktion.
               Bis 2026-09-09 standen hier zwei 30px-Knöpfe und sonst NICHTS: kein Umfang,
               keine Abteilung, kein Zeitraum. Die Fakten lagen die ganze Zeit im Frontend
               (ChatbotProposal.resolvedArguments) und wurden weggeworfen.
               Kit-Bausteine statt Eigenbau: .xds-card trägt Kopf/Körper/Fuß, und der Fuß
               stapelt am Telefon zentral (Kit-Regel .xds-card__footer, B3). -->
          <section
            *ngIf="turn.role === 'assistant' && turn.proposal && !turn.proposalHandled"
            class="xds-card proposal-card"
            data-testid="assistant-action-card"
          >
            <header class="xds-card__header">
              <div class="icon">
                <i class="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
              </div>
              <h3 class="xds-card__title">{{ previewFor(turn.proposal!).title }}</h3>
              <!-- Kit-Chip statt Eigenbau: --tint-warning-* gibt es NICHT (nur
                   --tint-primary-*), ein eigener Warn-Chip wäre ein dangling var() und
                   fiele still auf die geerbte Textfarbe zurück. -->
              <xds-chip
                class="proposal-card__kind"
                labelKey="HELP_CHAT.PROPOSAL.KIND_WRITE"
                tone="warning"
                [compact]="true"
                [interactive]="false"
              ></xds-chip>
            </header>
            <div class="xds-card__body">
              <dl class="proposal-card__fields" *ngIf="previewFor(turn.proposal!).fields.length">
                <div
                  class="proposal-card__field"
                  *ngFor="let f of previewFor(turn.proposal!).fields; trackBy: trackByFieldLabel"
                >
                  <dt>{{ f.label }}</dt>
                  <dd>{{ f.value }}</dd>
                </div>
              </dl>
            </div>
            <!-- ⚠️ DOM-Reihenfolge sekundär → primär: column-reverse im Kit-Fuß dreht die
                 Anzeige, der Primärknopf steht am Telefon damit OBEN. -->
            <footer class="xds-card__footer">
              <p class="proposal-card__note">
                {{ 'HELP_CHAT.PROPOSAL.DRAFT_HINT' | translate }}
              </p>
              <xds-button
                variant="secondary"
                [disabled]="chat.pending()"
                (clicked)="onConfirmProposal(turn.proposal!, false)"
              >
                {{ 'HELP_CHAT.PROPOSAL_CANCEL' | translate }}
              </xds-button>
              <xds-button
                variant="primary"
                iconLeft="fa-solid fa-check"
                [disabled]="chat.pending()"
                (clicked)="onConfirmProposal(turn.proposal!, true)"
              >
                {{ 'HELP_CHAT.PROPOSAL_CONFIRM' | translate }}
              </xds-button>
            </footer>
          </section>

          <!-- Sammel-Bestaetigung (mehrere Aktionen aus EINER Nachricht): eine Karte, eine
               Bestätigung. Die Einzelaktionen stehen als Liste statt als blosse Zahl. -->
          <section
            *ngIf="turn.role === 'assistant' && turn.proposalBatch && !turn.proposalHandled"
            class="xds-card proposal-card"
            data-testid="assistant-action-batch"
          >
            <header class="xds-card__header">
              <div class="icon">
                <i class="fa-solid fa-layer-group" aria-hidden="true"></i>
              </div>
              <h3 class="xds-card__title">
                {{
                  'HELP_CHAT.PROPOSAL.BATCH_TITLE'
                    | translate: { count: turn.proposalBatch!.items.length }
                }}
              </h3>
              <!-- Kit-Chip statt Eigenbau: --tint-warning-* gibt es NICHT (nur
                   --tint-primary-*), ein eigener Warn-Chip wäre ein dangling var() und
                   fiele still auf die geerbte Textfarbe zurück. -->
              <xds-chip
                class="proposal-card__kind"
                labelKey="HELP_CHAT.PROPOSAL.KIND_WRITE"
                tone="warning"
                [compact]="true"
                [interactive]="false"
              ></xds-chip>
            </header>
            <div class="xds-card__body">
              <ol class="proposal-card__list">
                <li *ngFor="let item of turn.proposalBatch!.items">{{ item.summary }}</li>
              </ol>
            </div>
            <footer class="xds-card__footer">
              <p class="proposal-card__note">
                {{ 'HELP_CHAT.PROPOSAL.DRAFT_HINT' | translate }}
              </p>
              <xds-button
                variant="secondary"
                [disabled]="chat.pending()"
                (clicked)="onConfirmBatch(turn.proposalBatch!, false)"
              >
                {{ 'HELP_CHAT.PROPOSAL_CANCEL' | translate }}
              </xds-button>
              <xds-button
                variant="primary"
                iconLeft="fa-solid fa-check"
                [disabled]="chat.pending()"
                (clicked)="onConfirmBatch(turn.proposalBatch!, true)"
              >
                {{ 'HELP_CHAT.PROPOSAL_CONFIRM' | translate }}
              </xds-button>
            </footer>
          </section>

          <!-- B2, zweiter Teil — nach dem Ausführen ein Weg ZURÜCK IN DIE ARBEIT.
               Bisher endete eine ausgeführte Schreibaktion in einem Satz Fließtext, und der
               Nutzer sass im Chatfenster fest.
               ⚠️ KEIN „Rückgängig": Das Backend hat keine Kompensation (D3 steht am Ende der
               Roadmap). Ein Knopf, der ein Versprechen gibt, das niemand einlöst, ist
               schlimmer als keiner. -->
          <section
            *ngIf="turn.role === 'assistant' && turn.executedTool"
            class="xds-card proposal-result"
            data-testid="assistant-action-result"
          >
            <header class="xds-card__header">
              <div class="icon">
                <i class="fa-solid fa-circle-check" aria-hidden="true"></i>
              </div>
              <h3 class="xds-card__title">
                {{
                  'HELP_CHAT.RESULT.TITLE'
                    | translate: { tool: (executedToolTitle(turn.executedTool!)) }
                }}
              </h3>
            </header>
            <!-- ⚠️ KEIN Körper mit Zahlen: „9 Schichten belegt, 3 offen" wäre erfunden.
                 Was wirklich passiert ist, steht im Antwortsatz des Backends direkt
                 darüber; hier steht nur der Weg zurück. Sobald das Backend ein
                 Ergebnis strukturiert liefert, gehören die Zahlen hierher. -->
            <footer class="xds-card__footer">
              <xds-button
                variant="secondary"
                iconLeft="fa-light fa-calendar-days"
                (clicked)="onOpenPlanner()"
              >
                {{ 'HELP_CHAT.RESULT.OPEN_PLAN' | translate }}
              </xds-button>
            </footer>
          </section>
        </ng-container>

        <app-help-thinking-indicator *ngIf="chat.pending()"></app-help-thinking-indicator>

        <!-- Demo: dezenter Hinweis, wenn nur noch wenige (≤3) KI-Anfragen uebrig sind. -->
        <xds-banner
          *ngIf="quotaLow"
          variant="warning"
          class="help-chat__quota-hint"
          [titleKey]="'DEMO.AI_QUOTA_LOW_TITLE'"
        >
          {{ 'DEMO.AI_QUOTA_LOW' | translate: { remaining: demoQuota!.remaining, quota: demoQuota!.quota } }}
          <button type="button" class="help-chat__quota-register" (click)="onRegisterFromQuota()">
            {{ 'DEMO.AI_LIMIT_REGISTER' | translate }}
          </button>
        </xds-banner>
      </div>
    </ng-template>
  </div>

  <!-- Persistente Schnellzugriffs-Leiste: bleibt WAEHREND einer laufenden Unterhaltung sichtbar
       (der Empty-State hat bereits seine eigene, ausfuehrlichere Beispiel-Liste) — verhindert,
       dass der Bildschirm nach der ersten Antwort leer/ratlos wirkt. -->
  <div
    class="help-chat__quick-rail"
    *ngIf="!chat.isEmpty()"
    role="group"
    [attr.aria-label]="'HELP_CHAT.QUICK_ACTIONS_LABEL' | translate"
  >
    <button
      type="button"
      class="help-chat__quick-chip"
      *ngFor="let key of visibleQuickActionKeys"
      (click)="onQuickAction(key)"
    >
      <i class="fa-solid fa-bolt" aria-hidden="true"></i>{{ key | translate }}
    </button>
  </div>

  <!-- C2 — Der Sprach-Entwurf: sichtbar, korrigierbar, verwerfbar.
       Bis 2026-09-09 ging das Transkript UNGEPRÜFT an die KI. Jetzt steht es hier, der
       erkannte Name ist markiert, und der Countdown ist mit jedem der drei Knöpfe
       abbrechbar — ein nicht abbrechbarer Countdown wäre nur eine Verzögerung. -->
  <section class="xds-card voice-draft" *ngIf="voiceDraft" data-testid="voice-draft">
    <header class="xds-card__header">
      <div class="icon"><i class="fa-solid fa-waveform-lines" aria-hidden="true"></i></div>
      <h3 class="xds-card__title">{{ 'HELP_CHAT.VOICE.DRAFT_TITLE' | translate }}</h3>
    </header>
    <div class="xds-card__body">
      <p class="voice-draft__text">
        <ng-container *ngFor="let seg of voiceDraft.segments">
          <mark class="voice-draft__mark" *ngIf="seg.marked; else plainSeg">{{ seg.text }}</mark>
          <ng-template #plainSeg>{{ seg.text }}</ng-template>
        </ng-container>
      </p>
      <p class="voice-draft__meta">
        <i class="fa-light fa-clock" aria-hidden="true"></i>
        {{ 'HELP_CHAT.VOICE.COUNTDOWN' | translate: { seconds: voiceDraft.secondsLeft } }}
      </p>
    </div>
    <footer class="xds-card__footer">
      <xds-button variant="ghost" (clicked)="closeVoiceDraft()">
        {{ 'HELP_CHAT.VOICE.DISCARD' | translate }}
      </xds-button>
      <xds-button variant="secondary" (clicked)="editVoiceDraft()">
        {{ 'HELP_CHAT.VOICE.EDIT' | translate }}
      </xds-button>
      <xds-button variant="primary" iconLeft="fa-solid fa-paper-plane" (clicked)="sendVoiceDraft()">
        {{ 'HELP_CHAT.VOICE.SEND_NOW' | translate }}
      </xds-button>
    </footer>
  </section>

  <!-- C3 — Die Erwähnungsliste. Erscheint über der Eingabezeile, sobald „@" plus drei
       Buchstaben getippt sind. Der Nutzer WÄHLT damit einen existierenden Namen, statt
       einen zu tippen — die Auflösung passiert vor dem Senden statt danach im Werkzeug,
       und der Spracheingabe nimmt es ihr grösstes Risiko.
       ⚠️ Ehrlich dazu: Gesendet wird weiterhin Text. Der Gewinn ist der EXISTIERENDE
       Name; eine echte ID-Übergabe wäre eine Vertragserweiterung. -->
  <div class="mention" *ngIf="mentionMatches.length" role="listbox">
    <div class="mention__head">
      {{
        (mentionKind === 'department'
          ? 'HELP_CHAT.MENTION.DEPARTMENTS'
          : 'HELP_CHAT.MENTION.EMPLOYEES'
        ) | translate: { query: mentionQuery }
      }}
    </div>
    <button
      xdsActionItem
      type="button"
      class="mention__row"
      role="option"
      *ngFor="let m of mentionMatches"
      [attr.aria-selected]="false"
      (click)="applyMention(m)"
    >
      <span class="mention__avatar" aria-hidden="true">{{ initialsOf(m.name) }}</span>
      <span class="mention__name">{{ m.name }}</span>
      <span class="mention__meta" *ngIf="m.meta">{{ m.meta }}</span>
    </button>
  </div>

  <!-- C3 — Kontextzeile: worauf sich „nächste Woche" bezieht. Ohne sie rät der Nutzer. -->
  <div class="context-line" *ngIf="!chat.isEmpty() || inputValue">
    <span class="context-line__label">{{ 'HELP_CHAT.CONTEXT_LABEL' | translate }}</span>
    <xds-chip
      [label]="currentWeekLabel"
      icon="fa-solid fa-calendar-week"
      [compact]="true"
      [interactive]="false"
    ></xds-chip>
    <xds-chip
      [labelKey]="'HELP_CHAT.CONTEXT_ALL_DEPARTMENTS'"
      icon="fa-solid fa-building"
      [compact]="true"
      [interactive]="false"
    ></xds-chip>
  </div>

  <footer class="help-chat__footer">
    <!-- Aufnahme laeuft: Wellenform-Streifen statt Eingabefeld (WhatsApp-Stil). -->
    <div
      *ngIf="voice.state() === 'recording'; else inputField"
      class="help-chat__recording"
      role="status"
      [attr.aria-label]="'HELP_CHAT.VOICE_RECORDING' | translate"
    >
      <span class="help-chat__rec-dot" aria-hidden="true"></span>
      <div class="help-chat__wave" aria-hidden="true">
        <span
          *ngFor="let l of voice.levels()"
          class="help-chat__wave-bar"
          [style.transform]="'scaleY(' + (0.12 + l * 0.88) + ')'"
        ></span>
      </div>
      <!-- Im Freihand-Modus ist „loslassen zum Senden" falsch — dort beendet ein zweiter
           Tipp auf das Mikro die Aufnahme. -->
      <span class="help-chat__rec-hint">{{
        (handsFree ? 'HELP_CHAT.VOICE.STOP_HINT' : 'HELP_CHAT.VOICE_RELEASE_HINT') | translate
      }}</span>
    </div>

    <ng-template #inputField>
      <xds-textarea
        #inputEl
        class="help-chat__input"
        [rows]="1"
        [autoGrow]="true"
        [maxHeightPx]="124"
        [(ngModel)]="inputValue"
        [ariaLabel]="'HELP_CHAT.INPUT_PLACEHOLDER' | translate"
        placeholderKey="HELP_CHAT.INPUT_PLACEHOLDER"
        (keydown)="onInputKey($event)"
        (ngModelChange)="onInputChanged($event)"
        [disabled]="chat.pending()"
      ></xds-textarea>
    </ng-template>
    <button
      type="button"
      class="help-chat__mic"
      onboardingAnchor="assistant-mic-button"
      [class.recording]="voice.state() === 'recording'"
      [class.transcribing]="voice.state() === 'transcribing'"
      [disabled]="chat.pending() || voice.state() === 'transcribing'"
      [attr.aria-label]="
        (handsFree ? 'HELP_CHAT.VOICE.STOP_HINT' : 'HELP_CHAT.VOICE_MIC_ARIA') | translate
      "
      [title]="'HELP_CHAT.VOICE.MIC_TITLE' | translate"
      (pointerdown)="onMicDown($event)"
      (pointermove)="onMicMove($event)"
      (pointerup)="onMicUp($event)"
      (pointercancel)="onMicCancel()"
      (contextmenu)="$event.preventDefault()"
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
        <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" />
        <path d="M17 11a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" />
      </svg>
    </button>
    <xds-icon-button
      variant="primary"
      icon="fa-solid fa-paper-plane"
      [disabled]="!inputValue.trim() || chat.pending()"
      [ariaLabel]="'HELP_CHAT.INPUT_PLACEHOLDER' | translate"
      (clicked)="onSubmit()"
    ></xds-icon-button>
  </footer>
</section>
